import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionSheet, type SheetAction } from '@/components/mobile/ActionSheet';
import { MBlock, MRow, MTitle } from '@/components/mobile/MobileKit';
import { AudioPlayer } from '@/components/ui/AudioPlayer';
import { Button } from '@/components/ui/Button';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { Modal } from '@/components/ui/Modal';
import { useRecordQueue, type RecordQueueView, type SendResult } from '@/hooks/useRecordQueue';
import { notifySuccess } from '@/lib/haptics';
import {
  canResend,
  formatRecordDuration,
  formatRecordedAt,
  type FailReason,
  type RejectedRecord,
  type RejectReason,
} from '@/lib/recordQueue';

/**
 * Неотправленные записи голоса (промпт 22; макеты v2 и «да» владельца 18.10.2026).
 *
 * ⚠️ МЕТОК У СТРОК НЕТ. В этом списке всё и так не отправлено: строка — урок, задание, длительность, во всю
 * ширину. Пока запись уходит — только полоска под строкой. Красной строкой под записью — только проблема:
 * «давно не отправлена» (Отправить / Удалить), «не удалось отправить» (Удалить), «работа уже принята»
 * (Понятно — строка уходит). Нажатие на строку — лист «Отправить сейчас / Прослушать / Удалить».
 *
 * Список один на телефон (экран `profile/records`) и на компьютер (карточка «Неотправленные работы»).
 */

const REJECT_TEXT: Record<RejectReason, string> = {
  already_reviewed: 'recordQueue.rejAlreadyReviewed',
  superseded: 'recordQueue.rejSuperseded',
  file_lost: 'recordQueue.rejFileLost',
  bad_recording: 'recordQueue.rejBadRecording',
};

const FAIL_TEXT: Record<FailReason, string> = {
  network: 'recordQueue.retryNetwork',
  server: 'recordQueue.retryServer',
  busy: 'recordQueue.retryBusy',
  session: 'recordQueue.retrySession',
  tooLarge: 'recordQueue.failTooLarge',
  forbidden: 'recordQueue.failForbidden',
  gone: 'recordQueue.failGone',
  unknown: 'recordQueue.failed',
};

function rowMeta(
  t: (k: string, o?: Record<string, unknown>) => string,
  r: { sectionTitle?: string; seconds?: number; at: number; lessonTitle?: string },
): string {
  const parts: string[] = [];
  if (r.sectionTitle) parts.push(r.sectionTitle);
  const dur = formatRecordDuration(r.seconds);
  if (dur) parts.push(dur);
  parts.push(r.lessonTitle ? t('recordQueue.recordedAt', { when: formatRecordedAt(r.at) }) : t('recordQueue.oldVersion'));
  return parts.join(' · ');
}

export function RecordQueueList({ onEmpty }: { onEmpty?: ReactNode }) {
  const { t } = useTranslation();
  const ask = useConfirm();
  const { rows, rejected, sendOne, sendAll, remove, dismiss, audioUrl } = useRecordQueue();
  const [sheet, setSheet] = useState<RecordQueueView | null>(null);
  const [listen, setListen] = useState<{ url: string | null; seconds?: number } | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const report = (res: SendResult) => {
    if (res === 'offline') setNote(t('recordQueue.offlineNow'));
    else if (res === 'sent') {
      setNote(null);
      notifySuccess();
    } else setNote(t('recordQueue.notYet'));
  };

  const send = async (id: string) => {
    setBusy(true);
    try {
      report(await sendOne(id));
    } finally {
      setBusy(false);
    }
  };

  const askRemove = async (id: string) => {
    if (await ask({ message: t('recordQueue.deleteConfirm'), confirmLabel: t('recordQueue.delete'), danger: true })) {
      await remove(id);
    }
  };

  const openListen = async (r: RecordQueueView) => {
    const url = await audioUrl(r.id);
    setListen({ url, seconds: r.seconds });
  };
  const closeListen = () => {
    if (listen?.url) URL.revokeObjectURL(listen.url);
    setListen(null);
  };

  if (rows.length === 0 && rejected.length === 0) return <>{onEmpty ?? null}</>;

  const sheetActions: SheetAction[] = sheet
    ? [
        ...(canResend(sheet)
          ? [{ label: t('recordQueue.sendNow'), icon: 'upload', onSelect: () => void send(sheet.id) }]
          : []),
        { label: t('recordQueue.listen'), icon: 'play_arrow', onSelect: () => void openListen(sheet) },
        { label: t('recordQueue.delete'), icon: 'delete', danger: true, onSelect: () => void askRemove(sheet.id) },
      ]
    : [];

  return (
    <div className="space-y-3" data-record-queue>
      <p className="px-0.5 text-[12.5px] leading-snug text-on-surface-variant">{t('recordQueue.hint')}</p>
      {rows.length > 0 && (
        <Button fullWidth disabled={busy} onClick={async () => {
          setBusy(true);
          try {
            report(await sendAll());
          } finally {
            setBusy(false);
          }
        }}>
          <span className="material-symbols-outlined">upload</span>
          {t('recordQueue.sendAll')}
        </Button>
      )}
      {note && (
        <p className="px-0.5 text-[12.5px] text-on-surface-variant" role="status" data-record-queue-note>
          {note}
        </p>
      )}
      <MBlock>
        {rows.map((r, i) => (
          <MRow
            key={r.id}
            wrap
            leading={<RowIcon />}
            title={r.lessonTitle ?? t('recordQueue.untitled')}
            meta={rowMeta(t, r)}
            onClick={() => setSheet(r)}
            last={i === rows.length - 1 && rejected.length === 0}
            below={<RowBelow row={r} onSend={() => void send(r.id)} onRemove={() => void askRemove(r.id)} busy={busy} />}
          />
        ))}
        {rejected.map((r, i) => (
          <MRow
            key={`rej:${r.blockId}`}
            wrap
            leading={<RowIcon />}
            title={r.lessonTitle ?? t('recordQueue.untitled')}
            meta={rowMeta(t, r)}
            last={i === rejected.length - 1}
            below={<RejectedBelow record={r} onDismiss={() => dismiss(r.blockId)} />}
          />
        ))}
      </MBlock>

      <ActionSheet
        open={!!sheet}
        onClose={() => setSheet(null)}
        title={sheet ? [sheet.lessonTitle ?? t('recordQueue.untitled'), formatRecordDuration(sheet.seconds)].filter(Boolean).join(' · ') : undefined}
        actions={sheetActions}
      />
      <Modal open={!!listen} onClose={closeListen} title={t('recordQueue.listen')} size="sm">
        {listen?.url ? (
          <AudioPlayer src={listen.url} knownSeconds={listen.seconds} />
        ) : (
          <p className="text-sm text-on-surface-variant">{t('recordQueue.playError')}</p>
        )}
      </Modal>
    </div>
  );
}

function RowIcon() {
  return (
    <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-surface-container-lowest text-on-surface-variant">
      <span className="material-symbols-outlined text-[18px]">mic</span>
    </span>
  );
}

function RowBelow({ row, onSend, onRemove, busy }: { row: RecordQueueView; onSend: () => void; onRemove: () => void; busy: boolean }) {
  const { t } = useTranslation();
  if (row.sending) {
    return (
      <div className="pb-3 pl-12" data-record-sending>
        <div className="h-1 overflow-hidden rounded-full bg-surface-container-lowest">
          <div
            className="h-full bg-info transition-[width]"
            style={{ width: `${row.percent ?? 30}%` }}
          />
        </div>
      </div>
    );
  }
  if (!row.problem) return null;
  // Временная причина или давняя запись — «Отправить» и «Удалить»; отказ навсегда — только «Удалить» (решение
  // владельца 18.10, таблица причин — docs/tasks/T4-record-queue.md).
  const canSend = canResend(row);
  const text =
    row.problem === 'stale'
      ? t('recordQueue.stale')
      : t(FAIL_TEXT[row.reason ?? 'unknown']);
  return (
    <div className="space-y-2 pb-3 pl-12" data-record-problem={row.problem} data-record-reason={row.reason}>
      <p className="text-[12px] leading-snug text-error">{text}</p>
      <div className="flex flex-wrap gap-2">
        {canSend && (
          <Button variant="secondary" disabled={busy} onClick={onSend}>
            {t('recordQueue.send')}
          </Button>
        )}
        <Button variant="secondary" className="text-error" onClick={onRemove}>
          {t('recordQueue.delete')}
        </Button>
      </div>
    </div>
  );
}

function RejectedBelow({ record, onDismiss }: { record: RejectedRecord; onDismiss: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2 pb-3 pl-12" data-record-rejected={record.reason}>
      <p className="text-[12px] leading-snug text-error">{t(REJECT_TEXT[record.reason])}</p>
      <Button variant="secondary" onClick={onDismiss}>
        {t('recordQueue.gotIt')}
      </Button>
    </div>
  );
}

/** Телефон: отдельный экран за строкой «Неотправленные записи» в настройках профиля. */
export function RecordQueuePage() {
  const { t } = useTranslation();
  const { rows } = useRecordQueue();
  // «Лежит на этом устройстве» — только то, что в очереди: непринятая запись с устройства уже снята (кадр 18.10).
  return (
    <div className="space-y-3 pb-6">
      <MTitle
        back="/app/profile/settings"
        title={t('recordQueue.title')}
        subtitle={rows.length > 0 ? t('recordQueue.subtitle', { count: rows.length }) : undefined}
      />
      <RecordQueueList
        onEmpty={<p className="px-0.5 text-[13px] text-on-surface-variant">{t('recordQueue.empty')}</p>}
      />
    </div>
  );
}
